
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { ApiTokens } from "./api-tokens/types";
import { ApiTokensService } from "./api-tokens/service";
import { getDefaultTokens, hasValidTokens } from "./api-tokens/utils";

export const useApiTokens = (brandId: string | null) => {
  const [apiTokens, setApiTokens] = useState<ApiTokens>(getDefaultTokens());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchApiTokens = useCallback(async (currentBrandId: string) => {
    if (!user || !currentBrandId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const tokens = await ApiTokensService.fetchTokens(currentBrandId);
      
      
      setApiTokens(tokens);
    } catch (err: any) {
      setError(err.message || 'Failed to load API tokens');
      // Don't reset to defaults on error, keep existing state
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const saveApiTokens = useCallback(async (newTokens: ApiTokens) => {
    if (!user || !brandId) {
      const errorMsg = "Please log in and select a brand to save API tokens";
      throw new Error(errorMsg);
    }

    try {
      await ApiTokensService.saveTokens(newTokens, brandId, user.id);
      
      // Update local state immediately after successful save
      setApiTokens(newTokens);
      setError(null); // Clear any previous errors
    } catch (error: any) {
      setError(error.message || 'Failed to save API tokens');
      throw error;
    }
  }, [user, brandId]);

  const updateTokenVerification = useCallback(async (type: 'openai' | 'gemini' | 'dataforseo' | 'firecrawl' | 'perplexity' | 'apify' | 'youtube' | 'claude' | 'hailuoai' | 'fal_ai' | 'byteplus' | 'wordpress' | 'apiflash' | 'heygen' | 'cartesia' | 'gamma' | 'pagespeed' | 'ghl' | 'slack' | 'xero' | 'domainr', isVerified: boolean) => {
    if (!user || !brandId) return;

    try {
      await ApiTokensService.updateVerification(type, isVerified, brandId, user.id);

      // Update local state with proper type handling
      setApiTokens(prev => {
        const updated = {
          ...prev,
          [`${type}_verified`]: isVerified,
          [`${type}_verified_at`]: isVerified ? new Date().toISOString() : undefined
        } as ApiTokens;
        return updated;
      });

    } catch (error) {
      // Error handled silently
    }
  }, [user, brandId]);

  useEffect(() => {
    if (brandId && user) {
      fetchApiTokens(brandId);
    } else {
      setApiTokens(getDefaultTokens());
      setError(null);
      setIsLoading(false);
    }
  }, [brandId, user, fetchApiTokens]);

  const updateApiTokens = useCallback((newTokens: ApiTokens) => {
    setApiTokens(newTokens);
  }, []);


  return {
    apiTokens,
    isLoading,
    error,
    hasValidTokens: hasValidTokens(apiTokens),
    updateApiTokens,
    saveApiTokens,
    updateTokenVerification,
    refetch: () => brandId && fetchApiTokens(brandId)
  };
};
